<template>
  <div>
    <SectionHeader :title="$t('orders.headline')">
      <template v-slot:breadcrumbs>
        <BreadCrumbs
          :items="[
            { text: $t('meta.orders'), name: 'orders' },
            { text: $t('meta.orderTemplates'), name: 'order_templates' },
          ]"
        />
      </template>

      <template v-slot:meta>
        <Link to="/order_templates/new" class="button button-green button-small button-round">
          {{ $t('orderTemplates.new') }}
        </Link>
      </template>
    </SectionHeader>

    <div id="order-templates">
      <div class="content-wrap">
        <header class="content-header cf">
          <h3>{{ $t('meta.orderTemplates') }}</h3>
        </header>

        <div class="content-body">
          <div v-if="loading">
            <LoadingSpinner />
          </div>

          <table v-if="!loading && orderTemplates.length > 0" id="order-templates-table">
            <thead>
              <tr class="order-templates-table__columns">
                <th>
                  <div class="column-header">
                    <div class="column-header__name"></div>
                  </div>
                </th>

                <th>
                  <div class="column-header">
                    <div class="column-header__name">{{ $t('orderTemplates.name') }}</div>
                  </div>
                </th>

                <th>
                  <div class="column-header">
                    <div class="column-header__name">{{ $t('orderTemplates.orderType') }}</div>
                  </div>
                </th>

                <th>
                  <div class="column-header">
                    <div class="column-header__name">{{ $t('orderTemplates.weekdays') }}</div>
                  </div>
                </th>

                <th>
                  <div class="column-header">
                    <div class="column-header__name">{{ $t('orderTemplates.vehicle') }}</div>
                  </div>
                </th>

                <!-- Actions -->
                <th></th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="orderTemplate in orderTemplates" :key="orderTemplate.uuid" class="order-templates-table__rows">
                <td class="icon">
                  <div :tooltip="tooltip(orderTemplate)" flow="left" style="display: inline">
                    <span
                      class="material-icons"
                      :class="{ active: !orderTemplate.paused, paused: orderTemplate.paused }"
                    >
                      {{ statusIcon(orderTemplate) }}
                    </span>
                  </div>
                </td>

                <td>{{ orderTemplate.name }}</td>
                <td>{{ orderTypeName(orderTemplate.order_type) }}</td>
                <td>{{ humanWeekdays(orderTemplate.weekdays) }}</td>
                <td>{{ orderTemplate.vehicle.name }}</td>

                <td>
                  <div class="order-template-actions">
                    <Link :to="`/order_templates/${orderTemplate.uuid}`" class="order-template-action">
                      <span class="material-icons">edit</span>
                    </Link>

                    <a @click="deleteOrderTemplate(orderTemplate)" class="order-template-action">
                      <span class="material-icons">delete</span>
                    </a>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <div v-if="!loading && pagination && pagination.total_pages > 1" class="pagination">
            <Pagination
              v-model="page"
              @paginate="page => loadOrderTemplates({ page })"
              :records="pagination.total_objects"
              :per-page="pagination.per_page"
              :options="{ chunk: 5, texts: { count: $t('pagination.text') } }"
            />
          </div>

          <div v-if="!loading && orderTemplates.length === 0" style="width: 100%" class="order-templates-table">
            <div>{{ $t('orderTemplates.none') }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Pagination from 'vue-pagination-2'

import BreadCrumbs from '@/components/BreadCrumbs'
import LoadingSpinner from '@/components/LoadingSpinner/index.vue'
import SectionHeader from '@/components/SectionHeader/index.vue'
import dateUtils from '@/components/mixins/dateUtils'

import orderTemplatesApi from '@/api/orderTemplates'

export default {
  name: 'OrderTemplatesView',
  mixins: [dateUtils],
  components: { BreadCrumbs, SectionHeader, LoadingSpinner, Pagination },
  inject: ['companyUser', 'showPopup'],
  data: () => ({
    loading: true,
    orderTemplates: [],
    page: 1,
    pagination: {},
  }),
  methods: {
    async loadOrderTemplates({ page, setLoading = true } = {}) {
      if (setLoading) this.loading = true
      if (page) this.page = page

      const response = await orderTemplatesApi.getPage({
        companyUuid: this.companyUser.company_uuid,
        page: this.page,
      })

      this.orderTemplates = response.orderTemplates
      this.pagination = response.pagination
      this.loading = false
    },
    orderTypeName(orderType) {
      const name = orderType.slice(0, -5).toLowerCase() // Cut off "Order" from "GatheringOrder" and downcase
      return this.$t('meta.orderTypes')[name]
    },
    humanWeekdays(weekdays) {
      const self = this
      return weekdays
        .map(function (weekday) {
          return self.$t(`meta.${weekday}`)
        })
        .join(', ')
    },
    async deleteOrderTemplate(orderTemplate) {
      const confirmAlert = await this.showPopup({
        content: this.$t('orderTemplates.deleteConfirmation'),
        type: 'confirm',
      })

      if (!confirmAlert) {
        return null
      } else {
        const response = await orderTemplatesApi.delete(orderTemplate.uuid)

        if (response.success) {
          if (this.orderTemplates.length === 1) {
            this.loadOrderTemplates({ page: 1 })
          } else {
            this.orderTemplates = this.orderTemplates.filter(item => item.uuid !== orderTemplate.uuid)
          }
        }
      }
    },
    statusIcon(orderTemplate) {
      if (orderTemplate.paused) {
        return 'pause_circle_outline'
      } else {
        return 'check_circle_outline'
      }
    },
    tooltip(orderTemplate) {
      if (orderTemplate.paused) {
        if (orderTemplate.continue_on) {
          const date = this.formattedDate(orderTemplate.continue_on)
          return this.$t('orderTemplates.pausedWithAutoContinue', { date: date })
        } else {
          return this.$t('orderTemplates.paused')
        }
      } else {
        return this.$t('orderTemplates.active')
      }
    },
  },
  async mounted() {
    this.loadOrderTemplates({ page: 1 })
  },
}
</script>

<style lang="scss" scoped>
#order-templates {
  display: flex;
  flex-direction: column;

  gap: 1rem;
  padding: 1.25rem;
}

#order-templates-table {
  width: 100%;

  & th,
  & td {
    text-align: left;
    padding: 10px 12px;
  }

  & th:last-of-type {
    text-align: center;
  }
}

.order-templates-table__columns {
  background-color: lightgray;

  th {
    font-weight: bold;
    padding: 0.5rem;

    .column-header {
      display: flex;
      gap: 0.5rem;
      justify-content: space-between;
      position: relative;
      &__name {
        display: flex;
        flex-direction: column;
        gap: 0.125rem;
      }
    }
  }
}

.icon {
  text-align: center !important;
  cursor: default;
  padding: 0 !important;

  .active {
    vertical-align: middle;
    color: #269a6c;
  }

  .paused {
    vertical-align: middle;
    color: grey;
  }
}

.order-template-actions {
  text-align: center;
}

.order-template-action {
  color: var(--primary-font-color);

  &:not(:last-child) {
    margin-right: 12px;
  }
}
</style>
